import DOMPurify from 'dompurify';
import hljs from 'highlight.js/lib/common';

function unescapeHtml(s) {
  return s.replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
}

/** Code block with a header (language label + copy button) and hljs highlighting. */
function renderCodeBlock(lang, escapedCode) {
  const raw = unescapeHtml(escapedCode).replace(/^\n+|\s+$/g, '');
  let html;
  let language = (lang || '').toLowerCase();
  try {
    if (language && hljs.getLanguage(language)) {
      html = hljs.highlight(raw, { language }).value;
    } else {
      const auto = hljs.highlightAuto(raw, ['bash', 'javascript', 'typescript', 'python', 'json', 'sql', 'yaml', 'xml', 'css', 'shell']);
      html = auto.value;
      language = language || auto.language || 'code';
    }
  } catch {
    html = escapedCode.trim();
    language = language || 'code';
  }
  // NOTE: code-block / code-copy are FUNCTIONAL hooks for the delegated copy
  // handler, not styling. Visual styling awaits the library's Chat.CodeBlock.
  return `<div class="code-block"><div><span>${language}</span>` +
    `<button type="button" class="code-copy" title="Скопировать"><span class="material-symbols-outlined code-copy-icon">content_copy</span></button></div>` +
    `<pre><code>${html}</code></pre></div>`;
}

export function formatElapsed(fromTs, toTs) {
  const sec = Math.max(0, Math.floor(((toTs || Date.now()) - fromTs) / 1000));
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}

export const TASK_STATE_META = {
  queued: { label: 'в очереди', color: 'var(--md-sys-color-outline)', icon: 'schedule' },
  running: { label: 'выполняется', color: 'var(--md-sys-color-primary)', icon: 'play_arrow' },
  waiting_for_human: { label: 'ждёт человека', color: 'var(--md-sys-color-tertiary)', icon: 'person' },
  waiting_for_approval: { label: 'ждёт разрешения', color: 'var(--md-sys-color-error)', icon: 'lock' },
  waiting_for_subtasks: { label: 'ждёт подзадачи', color: 'var(--md-sys-color-tertiary)', icon: 'account_tree' },
  review: { label: 'на проверке', color: 'var(--md-sys-color-tertiary)', icon: 'rate_review' },
  completed: { label: 'завершена', color: 'var(--md-sys-color-primary)', icon: 'check_circle' },
  failed: { label: 'ошибка', color: 'var(--md-sys-color-error)', icon: 'error' },
  cancelled: { label: 'отменена', color: 'var(--md-sys-color-outline)', icon: 'cancel' },
  timed_out: { label: 'таймаут', color: 'var(--md-sys-color-error)', icon: 'timer_off' },
};

export function taskStateMeta(state) {
  return TASK_STATE_META[state] || { label: state, color: 'var(--md-sys-color-outline)', icon: 'help' };
}

/** Состояния процесса (lib/core/states.js RUN_STATES) — глиф несёт исход, а не украшает строку. */
export const RUN_STATE_META = {
  starting: { label: 'запускается', icon: 'hourglass_top' },
  running: { label: 'выполняется', icon: 'play_arrow' },
  completed: { label: 'завершён', icon: 'check_circle' },
  failed: { label: 'ошибка', icon: 'error' },
  cancelled: { label: 'отменён', icon: 'cancel' },
  timed_out: { label: 'таймаут', icon: 'timer_off' },
  lost: { label: 'потерян', icon: 'link_off' },
};

export function runStateMeta(state) {
  return RUN_STATE_META[state] || { label: state, icon: 'help' };
}

export function renderMarkdown(text) {
  if (!text) return '';
  let t = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  t = t.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => renderCodeBlock(lang, code));
  t = t.replace(/`([^`]+)`/g, '<code>$1</code>');
  t = t.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  t = t.replace(/\*(.+?)\*/g, '<em>$1</em>');
  t = t.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  t = t.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  t = t.replace(/^# (.+)$/gm, '<h2>$1</h2>');
  t = t.replace(/^- (.+)$/gm, '<li>$1</li>');
  t = t.replace(/(^|[^\w@])@([a-zA-Z0-9][a-zA-Z0-9_-]{0,63})/g, '$1<strong>@$2</strong>');
  t = t.replace(/\n\n/g, '</p><p>');
  t = t.replace(/\n/g, '<br>');
  if (t.includes('</p>')) t = '<p>' + t + '</p>';
  return DOMPurify.sanitize(t, { ADD_ATTR: ['title'] });
}

/**
 * Delegated click handler for rendered markdown: the copy button in code
 * block headers. Attach once on the message list container.
 */
export function handleRenderedContentClick(e) {
  const btn = e.target.closest && e.target.closest('.code-copy');
  if (!btn) return;
  const block = btn.closest('.code-block');
  const code = block && block.querySelector('code');
  if (!code) return;
  const text = code.innerText;
  const done = () => {
    const icon = btn.querySelector('.code-copy-icon');
    if (!icon) return;
    icon.textContent = 'check';
    btn.classList.add('code-copy-done');
    setTimeout(() => { icon.textContent = 'content_copy'; btn.classList.remove('code-copy-done'); }, 1500);
  };
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
  } else {
    fallbackCopy(text, done);
  }
}

function fallbackCopy(text, done) {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand('copy'); done(); } catch {}
  document.body.removeChild(ta);
}
